import { getUrlParams, getFirstValue } from "./urlParams";
import type LxTracker from "@/lib/lx";
import { Level, reportError } from "./owl";
import { reportPerfMetric } from "./owl";
import { getFirstCookieClient, getFirstCookieFromHeader } from "./cookie";
import { getAppEnv } from "./getBrowserEnv";

// ============================================
// UUID 常量
// ============================================

/** Query 中 uuid 的多种写法，按数组顺序优先：uuid */
export const UUID_QUERY_KEYS = ["uuid"] as const;
/** Cookie 中 uuid 的多种写法，按数组顺序优先：iuuid → uuid → lxcuid */
export const UUID_COOKIE_KEYS = ["iuuid", "uuid", "lxcuid"] as const;

// ============================================
// UUID 公共函数
// ============================================

/**
 * 生成随机字符串作为 uuid 兜底
 */
export function generateRandomUuid(): string {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * 从 URL 参数获取 uuid 字符串（CSR/SSR 通用）
 * @param query 可选，SSR 传入 context.query；CSR 不传则从 window.location 解析
 */
export function getUuidFromUrl(
  query?: Record<string, string | undefined>,
): string | null {
  // 美小环境：URL 上的 uuid 不可靠（美小会传 openId），跳过
  // 仅客户端判断，SSR 端由 getUuidForSSR 通过 ua 参数单独处理
  if (typeof window !== "undefined") {
    const { isMTWXApp } = getAppEnv();
    if (isMTWXApp) return null;
  }

  const urlParams = query || getUrlParams();
  const value = getFirstValue(urlParams, UUID_QUERY_KEYS);
  return value ?? null;
}

export function getUuidFromCookie(): string | undefined {
  return getFirstCookieClient(UUID_COOKIE_KEYS);
}

/**
 * 从请求的 query 和 cookie 字符串获取 uuid 字符串（SSR 内部使用）
 */
function getUuidStringFromRequest(
  query?: Record<string, string | undefined> | null,
  cookie?: string | null,
): string | undefined {
  return (
    getFirstValue(query ?? {}, UUID_QUERY_KEYS) ??
    getFirstCookieFromHeader(cookie ?? "", UUID_COOKIE_KEYS)
  );
}

/**
 * SSR 环境获取 uuid 字符串
 * 优先级：URL 参数 uuid（美小除外，美小 uuid 传的是 openId）> cookie.iuuid > cookie.uuid > cookie.lxcuid > 随机生成
 * @param query SSR context 中的 query 参数
 * @param cookie 可选，SSR 时传入 context.cookie，优先用 getUserIdStringFromRequest 取 userId 作为 uuid
 */
export function getUuidForSSR(
  query?: Record<string, string | undefined>,
  cookie?: string | null,
  ua?: string,
): string {
  const isMTWXApp = ua ? getAppEnv(ua).isMTWXApp : false;
  const result = cookie != null ? getUuidStringFromRequest(isMTWXApp ? undefined : query, cookie) : undefined;
  return result || generateRandomUuid();
}

/**
 * 从 tracker 获取 UUID，失败则随机生成
 */
export async function getUuidWithFallback(tracker: LxTracker | null): Promise<string> {
  if (tracker) {
    try {
      const trackerUuid = await tracker.getUuid();
      if (trackerUuid?.trim()) {
        // 上报性能埋点 perf
        reportPerfMetric({
          key: "getUuidByTrackerCost",
          value: Date.now() - (window as any)?.__PAGE_JS_START__,
        });

        return trackerUuid;
      }
    } catch (error) {
      reportError({
        name: "[uuid] getUuidWithFallbackError",
        msg: error,
        category: "baseError",
        level: Level.WARN,
      });
      return generateRandomUuid();
    }
  }
  return generateRandomUuid();
}

// 只缓存 uuid 字符串（页面生命周期内单例，懒求值）
let cachedUuid: string | null = null;

/** tracker.getUuid() 已 resolve 时写入，供 getUuid 同步判断 */
let trackerUuidResolved: string | null = null;

/** App 入口调用，启动 tracker.getUuid() 请求，不阻塞 */
export function initUuidTracker(tracker: LxTracker | null): void {
  if (!tracker) return;
  getUuidWithFallback(tracker).then((uuid) => {
    if (uuid?.trim()) {
      trackerUuidResolved = uuid;
      // if(cachedUuid){

      // }
    }
  });
}

/** SSR 场景：客户端注入服务端下发的 uuid 字符串 */
export function initUuidFromSSR(uuid: string | null): void {
  if (uuid) {
    cachedUuid = uuid;
  }
}

/**
 * 获取当前页面的 uuid 字符串：懒求值 + 单例，被调用时立即返回
 * 优先级：① 已有缓存（含 SSR 注入）② URL 参数（userId/uuid）③ tracker 已 resolve ④ 随机生成
 */
export function getUuid(): string {
  if (cachedUuid) {
    return cachedUuid;
  }
  const fromUrl = getUuidFromUrl();
  if (fromUrl) {
    cachedUuid = fromUrl;
    return fromUrl;
  }
  if (trackerUuidResolved) {
    cachedUuid = trackerUuidResolved;
    return trackerUuidResolved;
  }
  const fromCookie = getUuidFromCookie();
  if (fromCookie) {
    cachedUuid = fromCookie;
    return fromCookie;
  }
  cachedUuid = generateRandomUuid();
  return cachedUuid;
}

/**
 * 同步读取已缓存的 uuid 字符串（可能为 null）
 * 需要 uuid 时请使用 getUuid()
 */
export function getCacheUuid(): string | null {
  return cachedUuid;
}
